import { TertiaryButton } from '@theme/components/Button/src/tsx/Button';
import { useLayoutEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useCyclicNavigation } from '../hooks/useCyclicNavigation';
import { useModalDialog } from '../hooks/useModalDialog';
import type { Feature, FeatureCategory, FeatureEdition, GetLabel, ShowLightbox } from '../types/feature';
import ListItem from './ListItem';

type DialogProps = {
	feature: Feature | null;
	isOpen: boolean;
	index: number;
	total: number;
	onNavigate: (index: number) => void;
	onClose: () => void;
	categories: Record<string, FeatureCategory>;
	editions: FeatureEdition[];
	getLabel: GetLabel;
	showLightbox: ShowLightbox;
};

const getPlainText = (html: string): string => new DOMParser().parseFromString(html, 'text/html').body.textContent ?? '';

export default function Dialog({ feature, isOpen, index, total, onNavigate, onClose, categories, editions, getLabel, showLightbox }: DialogProps) {
	const { dialogRef, setDialogRef, close, handleBackdropClick, handleCancel } = useModalDialog({ isOpen });
	const closeButtonRef = useRef<HTMLButtonElement>(null);

	useLayoutEffect(() => {
		if (dialogRef.current?.open && document.activeElement === document.body) {
			closeButtonRef.current?.focus({ preventScroll: true });
		}
	}, [feature, index, dialogRef]);

	const { hasNavigation, goToPrevious, goToNext, handleKeyDown } = useCyclicNavigation({
		index,
		total,
		onNavigate,
		scrollRef: dialogRef,
	});

	const label = useMemo(() => (feature ? getPlainText(feature.name) : undefined), [feature]);

	return createPortal(
		<dialog
			ref={setDialogRef}
			className="feature-dialog"
			aria-label={label}
			onClose={onClose}
			onCancel={handleCancel}
			onClick={handleBackdropClick}
			onKeyDown={handleKeyDown}
		>
			{feature && (
				<div className="feature-dialog__inner">
					<ListItem
						key={feature.id ?? index}
						feature={feature}
						categories={categories}
						getLabel={getLabel}
						editions={editions}
						showLightbox={showLightbox}
						variant="dialog"
						headerAction={
							<button ref={closeButtonRef} type="button" className="feature-dialog__close" aria-label={getLabel('close')} onClick={close}>
								<span aria-hidden="true">close</span>
							</button>
						}
					/>

					{hasNavigation && (
						<nav className="feature-dialog__nav">
							<TertiaryButton onClick={goToPrevious}>
								<span className="feature-dialog__nav-icon" aria-hidden="true">
									keyboard_arrow_left
								</span>
								<span className="feature-dialog__nav-label">{getLabel('previous')}</span>
							</TertiaryButton>
							<span className="feature-dialog__nav-count" aria-live="polite">
								{index + 1} / {total}
							</span>
							<TertiaryButton onClick={goToNext}>
								<span className="feature-dialog__nav-label">{getLabel('next')}</span>
								<span className="feature-dialog__nav-icon" aria-hidden="true">
									keyboard_arrow_right
								</span>
							</TertiaryButton>
						</nav>
					)}
				</div>
			)}
		</dialog>,
		document.body,
	);
}
